import React, { useState, useEffect } from 'react';
import { useTreeAuth } from '../../lib/treeAuth';
import { updateTreeSettings } from '../../lib/treeApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Package, Truck, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function TreeSettingsPage() {
  const { settings, refreshSettings } = useTreeAuth();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const handleSave = async () => {
    const treesPerPallet = parseInt(form.trees_per_pallet, 10);
    const palletsPerTrailer = parseInt(form.pallets_per_trailer, 10);
    if (!treesPerPallet || treesPerPallet < 1) return toast.error('Trees per pallet must be at least 1');
    if (!palletsPerTrailer || palletsPerTrailer < 1) return toast.error('Pallets per trailer must be at least 1');
    setSaving(true);
    try {
      await updateTreeSettings({ trees_per_pallet: treesPerPallet, pallets_per_trailer: palletsPerTrailer });
      await refreshSettings();
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="tree-settings-page">
      <div>
        <h1 className="text-3xl font-bold text-[#0A1711] font-['Outfit'] tracking-tight">Settings</h1>
        <p className="text-sm text-[#4A5D53] mt-1">Numbers used across the dashboard, field app and manifests</p>
      </div>

      <div className="bg-white border border-[#1A4331]/15 rounded-xl p-6 max-w-lg space-y-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#1A4331]/10 flex items-center justify-center flex-shrink-0">
            <Package className="w-5 h-5 text-[#1A4331]" />
          </div>
          <div className="flex-1">
            <Label htmlFor="trees-per-pallet" className="text-[#0A1711] font-semibold">Trees per Pallet</Label>
            <p className="text-xs text-[#4A5D53] mb-2">Used to convert pallet counts into tree counts everywhere in the app</p>
            <Input
              id="trees-per-pallet"
              type="number"
              min={1}
              value={form.trees_per_pallet}
              onChange={(e) => setForm({ ...form, trees_per_pallet: e.target.value })}
              className="max-w-[160px]"
              data-testid="tree-settings-trees-per-pallet-input"
            />
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#1A4331]/10 flex items-center justify-center flex-shrink-0">
            <Truck className="w-5 h-5 text-[#1A4331]" />
          </div>
          <div className="flex-1">
            <Label htmlFor="pallets-per-trailer" className="text-[#0A1711] font-semibold">Pallets per Full Trailer</Label>
            <p className="text-xs text-[#4A5D53] mb-2">How many pallets ready in the field counts as a full load for collection</p>
            <Input
              id="pallets-per-trailer"
              type="number"
              min={1}
              value={form.pallets_per_trailer}
              onChange={(e) => setForm({ ...form, pallets_per_trailer: e.target.value })}
              className="max-w-[160px]"
              data-testid="tree-settings-pallets-per-trailer-input"
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-[#1A4331] text-white hover:bg-[#1A4331]/90" data-testid="tree-settings-save-button">
          <Save className="w-4 h-4 mr-2" /> Save Settings
        </Button>
      </div>
    </div>
  );
}
